import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { clientsAPI, usersAPI, assignmentsAPI } from '../services/api';
import { US_TIMEZONES } from '../services/timezone';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { 
  Users, 
  Plus, 
  MagnifyingGlass,
  Phone,
  MapPin,
  FirstAid,
  Warning,
  XCircle,
  ArrowCounterClockwise,
  Calendar
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { format } from 'date-fns';

const ClientsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [clientUsers, setClientUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [statusFilter, setStatusFilter] = useState('active');
  const [isDischargeOpen, setIsDischargeOpen] = useState(false);
  const [dischargingClient, setDischargingClient] = useState(null);
  const [dischargeForm, setDischargeForm] = useState({ discharge_date: '', discharge_reason: '' });
  const [formData, setFormData] = useState({
    user_id: '',
    name: '',
    date_of_birth: '',
    address: '',
    email: '',
    emergency_contact: '',
    emergency_phone: '',
    emergency_email: '',
    medical_conditions: '',
    allergies: '',
    notes: '',
    timezone: 'America/New_York',
    start_date: ''
  });

  useEffect(() => {
    loadData();
  }, [statusFilter]);

  const loadData = async () => {
    try {
      const [clientsRes, usersRes] = await Promise.all([
        clientsAPI.getAll(statusFilter === 'all' ? undefined : statusFilter),
        user?.role === 'supervisor' ? usersAPI.getAll('client') : Promise.resolve({ data: [] })
      ]);
      setClients(clientsRes.data);
      setClientUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading clients:', error);
      toast.error('Failed to load clients');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateClient = async (e) => {
    e.preventDefault();
    try {
      const data = {
        ...formData,
        medical_conditions: formData.medical_conditions.split(',').map(s => s.trim()).filter(Boolean),
        allergies: formData.allergies.split(',').map(s => s.trim()).filter(Boolean)
      };
      await clientsAPI.create(data);
      toast.success('Client profile created successfully');
      setIsCreateOpen(false);
      setFormData({
        user_id: '',
        name: '',
        date_of_birth: '',
        address: '',
        email: '',
        emergency_contact: '',
        emergency_phone: '',
        emergency_email: '',
        medical_conditions: '',
        allergies: '',
        notes: '',
        timezone: 'America/New_York',
        start_date: ''
      });
      loadData();
    } catch (error) {
      toast.error('Failed to create client profile');
    }
  };

  const handleDischarge = async (e) => {
    e.preventDefault();
    if (!dischargingClient) return;
    try {
      await clientsAPI.discharge(dischargingClient.id, dischargeForm);
      toast.success(`${dischargingClient.name} has been discharged`);
      setIsDischargeOpen(false);
      setDischargingClient(null);
      setDischargeForm({ discharge_date: '', discharge_reason: '' });
      loadData();
    } catch (error) { toast.error('Failed to discharge client'); }
  };

  const handleReadmit = async (clientId) => {
    try {
      await clientsAPI.readmit(clientId);
      toast.success('Client readmitted');
      loadData();
    } catch (error) { toast.error('Failed to readmit client'); }
  };

  const openDischarge = (client, e) => {
    e.stopPropagation();
    setDischargingClient(client);
    setDischargeForm({ discharge_date: format(new Date(), 'yyyy-MM-dd'), discharge_reason: '' });
    setIsDischargeOpen(true);
  };

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.address?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 fade-in" data-testid="clients-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl md:text-3xl font-semibold text-foreground">
            Clients
          </h1>
          <p className="text-muted-foreground mt-1">
            Manage client profiles and care information
          </p>
        </div>
        {user?.role === 'supervisor' && (
          <Dialog open={isCreateOpen} onOpenChange={setIsCreateOpen}>
            <DialogTrigger asChild>
              <Button className="rounded-xl gap-2" data-testid="add-client-btn">
                <Plus weight="bold" className="w-4 h-4" />
                Add Client
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">Create Client Profile</DialogTitle>
                <DialogDescription>
                  Add a new client to the system
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateClient} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Link to User Account (Optional)</Label>
                  <Select value={formData.user_id} onValueChange={(value) => setFormData({...formData, user_id: value})}>
                    <SelectTrigger className="rounded-xl" data-testid="client-user-select">
                      <SelectValue placeholder="Select user account" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No linked account</SelectItem>
                      {clientUsers.map(u => (
                        <SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="John Doe"
                    required
                    className="rounded-xl"
                    data-testid="client-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Date of Birth</Label>
                  <Input
                    type="date"
                    value={formData.date_of_birth}
                    onChange={(e) => setFormData({...formData, date_of_birth: e.target.value})}
                    className="rounded-xl"
                    data-testid="client-dob-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Start Date (Service Begin)</Label>
                  <Input
                    type="date"
                    value={formData.start_date}
                    onChange={(e) => setFormData({...formData, start_date: e.target.value})}
                    className="rounded-xl"
                    data-testid="client-start-date-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Address</Label>
                  <Textarea
                    value={formData.address}
                    onChange={(e) => setFormData({...formData, address: e.target.value})}
                    placeholder="123 Main St, City, State 12345"
                    className="rounded-xl"
                    data-testid="client-address-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Time Zone <span className="text-xs text-muted-foreground">(used for all clock in/out times)</span></Label>
                  <Select
                    value={formData.timezone}
                    onValueChange={(v) => setFormData({ ...formData, timezone: v })}
                  >
                    <SelectTrigger className="rounded-xl" data-testid="client-timezone-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {US_TIMEZONES.map(tz => (
                        <SelectItem key={tz.value} value={tz.value}>{tz.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Email <span className="text-xs text-muted-foreground">(used for invoice delivery)</span></Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({...formData, email: e.target.value})}
                    placeholder="client@example.com"
                    className="rounded-xl"
                    data-testid="client-email-input"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Emergency Contact</Label>
                    <Input
                      value={formData.emergency_contact}
                      onChange={(e) => setFormData({...formData, emergency_contact: e.target.value})}
                      placeholder="Jane Doe (Daughter)"
                      className="rounded-xl"
                      data-testid="client-emergency-contact-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Emergency Phone</Label>
                    <Input
                      value={formData.emergency_phone}
                      onChange={(e) => setFormData({...formData, emergency_phone: e.target.value})}
                      placeholder="+1 555-0000"
                      className="rounded-xl"
                      data-testid="client-emergency-phone-input"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label>Emergency Contact Email <span className="text-xs text-muted-foreground">(optional — fallback for invoices)</span></Label>
                  <Input
                    type="email"
                    value={formData.emergency_email}
                    onChange={(e) => setFormData({...formData, emergency_email: e.target.value})}
                    placeholder="family-member@example.com"
                    className="rounded-xl"
                    data-testid="client-emergency-email-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Medical Conditions (comma-separated)</Label>
                  <Textarea
                    value={formData.medical_conditions}
                    onChange={(e) => setFormData({...formData, medical_conditions: e.target.value})}
                    placeholder="Diabetes, Hypertension"
                    className="rounded-xl"
                    data-testid="client-conditions-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Allergies (comma-separated)</Label>
                  <Input
                    value={formData.allergies}
                    onChange={(e) => setFormData({...formData, allergies: e.target.value})}
                    placeholder="Penicillin, Shellfish"
                    className="rounded-xl"
                    data-testid="client-allergies-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Additional Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    placeholder="Any additional information..."
                    className="rounded-xl"
                    data-testid="client-notes-input"
                  />
                </div>

                <div className="flex gap-3 pt-4">
                  <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsCreateOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" className="flex-1 rounded-xl" data-testid="create-client-submit">
                    Create Profile
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search & Filter */}
      <div className="flex flex-col md:flex-row gap-4">
        <div className="relative flex-1 max-w-md">
          <MagnifyingGlass className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
          <Input
            placeholder="Search clients..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 rounded-xl"
            data-testid="client-search-input"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-48 rounded-xl" data-testid="client-status-filter">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="active">Active Clients</SelectItem>
            <SelectItem value="discharged">Discharged Clients</SelectItem>
            <SelectItem value="all">All Clients</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Discharge Dialog */}
      <Dialog open={isDischargeOpen} onOpenChange={(open) => { if (!open) { setIsDischargeOpen(false); setDischargingClient(null); } }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading">Discharge Client</DialogTitle>
            <DialogDescription>
              {dischargingClient && `Discharge ${dischargingClient.name} from active services`}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleDischarge} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label>Discharge Date *</Label>
              <Input type="date" value={dischargeForm.discharge_date} onChange={(e) => setDischargeForm({ ...dischargeForm, discharge_date: e.target.value })} required className="rounded-xl" data-testid="discharge-date-input" />
            </div>
            <div className="space-y-2">
              <Label>Reason for Discharge</Label>
              <Textarea value={dischargeForm.discharge_reason} onChange={(e) => setDischargeForm({ ...dischargeForm, discharge_reason: e.target.value })} placeholder="Reason for discharge..." rows={3} className="rounded-xl" data-testid="discharge-reason-input" />
            </div>
            <div className="flex gap-3 pt-4">
              <Button type="button" variant="outline" className="flex-1 rounded-xl" onClick={() => setIsDischargeOpen(false)}>Cancel</Button>
              <Button type="submit" className="flex-1 rounded-xl bg-destructive hover:bg-destructive/90" data-testid="confirm-discharge-btn">Discharge</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Clients Grid */}
      {filteredClients.length === 0 ? (
        <Card className="rounded-2xl border border-border">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users weight="duotone" className="w-16 h-16 text-muted-foreground/50 mb-4" />
            <h3 className="font-heading text-lg font-medium">No clients found</h3>
            <p className="text-muted-foreground text-sm mt-1">
              {searchTerm ? 'Try a different search term' : 'Add your first client to get started'}
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredClients.map((client) => (
            <Card 
              key={client.id} 
              className="rounded-2xl border border-border hover:border-primary/30 hover:shadow-md transition-all duration-200 cursor-pointer"
              onClick={() => navigate(`/clients/${client.id}`)}
              data-testid={`client-card-${client.id}`}
            >
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${client.status === 'discharged' ? 'bg-gray-200' : 'bg-primary/10'}`}>
                      <Users weight="duotone" className={`w-6 h-6 ${client.status === 'discharged' ? 'text-gray-400' : 'text-primary'}`} />
                    </div>
                    <div>
                      <CardTitle className={`font-heading text-lg ${client.status === 'discharged' ? 'text-muted-foreground' : ''}`}>{client.name}</CardTitle>
                      {client.date_of_birth && (
                        <CardDescription>DOB: {client.date_of_birth}</CardDescription>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1">
                    <Badge className={`rounded-full text-xs ${client.status === 'discharged' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                      {client.status || 'active'}
                    </Badge>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {client.start_date && (
                  <div className="flex items-center gap-2 text-sm">
                    <Calendar weight="duotone" className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">Started: {client.start_date}</span>
                  </div>
                )}
                {client.discharge_date && (
                  <div className="flex items-center gap-2 text-sm">
                    <XCircle weight="duotone" className="w-4 h-4 text-destructive" />
                    <span className="text-muted-foreground">Discharged: {client.discharge_date}{client.discharge_reason ? ` - ${client.discharge_reason}` : ''}</span>
                  </div>
                )}
                {client.address && (
                  <div className="flex items-start gap-2 text-sm">
                    <MapPin weight="duotone" className="w-4 h-4 text-muted-foreground mt-0.5" />
                    <span className="text-muted-foreground">{client.address}</span>
                  </div>
                )}
                
                {client.emergency_phone && (
                  <div className="flex items-center gap-2 text-sm">
                    <Phone weight="duotone" className="w-4 h-4 text-muted-foreground" />
                    <span className="text-muted-foreground">
                      {client.emergency_contact}: {client.emergency_phone}
                    </span>
                  </div>
                )}

                {client.medical_conditions?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <FirstAid weight="duotone" className="w-4 h-4 text-muted-foreground mt-1" />
                    <div className="flex flex-wrap gap-1">
                      {client.medical_conditions.map((condition, idx) => (
                        <Badge key={idx} variant="secondary" className="text-xs rounded-full">
                          {condition}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {client.allergies?.length > 0 && (
                  <div className="flex items-start gap-2">
                    <Warning weight="duotone" className="w-4 h-4 text-destructive mt-1" />
                    <div className="flex flex-wrap gap-1">
                      {client.allergies.map((allergy, idx) => (
                        <Badge key={idx} variant="destructive" className="text-xs rounded-full bg-destructive/10 text-destructive">
                          {allergy}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )}

                {/* Discharge / Readmit Buttons */}
                {user?.role === 'supervisor' && (
                  <div className="pt-2">
                    {client.status !== 'discharged' ? (
                      <Button variant="outline" size="sm" className="rounded-xl gap-1 text-destructive border-destructive/30 hover:bg-destructive/10" onClick={(e) => openDischarge(client, e)} data-testid={`discharge-btn-${client.id}`}>
                        <XCircle weight="bold" className="w-3 h-3" /> Discharge
                      </Button>
                    ) : (
                      <Button variant="outline" size="sm" className="rounded-xl gap-1 text-green-700 border-green-300 hover:bg-green-50" onClick={(e) => { e.stopPropagation(); handleReadmit(client.id); }} data-testid={`readmit-btn-${client.id}`}>
                        <ArrowCounterClockwise weight="bold" className="w-3 h-3" /> Readmit
                      </Button>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ClientsPage;
